/** `/register` — open registration; new accounts await approval. */
import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { useRegister } from '@/hooks/useAuthApi'
import { useAuthStore } from '@/stores/auth'

interface FormValues {
  email: string
  password: string
  confirm: string
}

export default function Register() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore.getState()
  const register_ = useRegister()

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(user?.is_active ? '/app' : '/pending', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    register, handleSubmit, watch, formState: { errors },
  } = useForm<FormValues>()
  const password = watch('password')

  async function onSubmit(values: FormValues) {
    try {
      const data = await register_.mutateAsync({
        email: values.email, password: values.password,
      })
      navigate(data.user.is_active ? '/app' : '/pending', { replace: true })
    } catch { /* error shown below */ }
  }

  function localisedApiError(detail: unknown): string {
    if (typeof detail !== 'string') return t('auth:errors.signup_failed')
    if (detail.toLowerCase().includes('email already in use')) {
      return t('auth:errors.email_taken')
    }
    return detail
  }

  const apiError =
    register_.isError && isAxiosError(register_.error)
      ? localisedApiError(register_.error.response?.data?.detail)
      : null

  return (
    <AuthLayout
      title={t('auth:register.title')}
      subtitle={t('auth:register.subtitle')}
      footer={
        <>
          {t('auth:register.have_account')}{' '}
          <Link className="text-fire hover:underline" to="/login">
            {t('auth:register.log_in')}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          label={t('auth:register.email_label')}
          htmlFor="email" error={errors.email?.message}
        >
          <Input
            id="email"
            type="email"
            autoFocus
            autoComplete="email"
            placeholder={t('auth:register.email_placeholder')}
            invalid={!!errors.email}
            {...register('email', {
              required: t('auth:validation.email_required'),
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: t('auth:validation.email_invalid'),
              },
            })}
          />
        </FormField>

        <FormField
          label={t('auth:register.password_label')}
          htmlFor="password"
          hint={t('auth:register.password_hint')}
          error={errors.password?.message}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            invalid={!!errors.password}
            {...register('password', {
              required: t('auth:validation.password_required'),
              minLength: { value: 8, message: t('auth:validation.password_min') },
              maxLength: { value: 128, message: t('auth:validation.password_max') },
            })}
          />
        </FormField>

        <FormField
          label={t('auth:register.confirm_label')}
          htmlFor="confirm" error={errors.confirm?.message}
        >
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            invalid={!!errors.confirm}
            {...register('confirm', {
              required: t('auth:validation.confirm_required'),
              validate: (v) =>
                v === password || (t('auth:validation.confirm_mismatch') as string),
            })}
          />
        </FormField>

        {apiError && (
          <div className="bg-rose-faint text-rose text-sm rounded px-3 py-2">
            {apiError}
          </div>
        )}

        <Button type="submit" className="w-full" loading={register_.isPending}>
          {t('auth:register.submit')}
        </Button>

        <p className="text-xs text-textFaint text-center pt-2">
          {t('auth:register.disclaimer')}
        </p>
      </form>
    </AuthLayout>
  )
}
